import { useState, useRef, useEffect, useCallback } from 'react';
import { Lead } from '../../../types';
import { useLeads } from '../../../contexts/leads/LeadsContext';
import { createModuleLogger } from '../../../utils/logger';

const log = createModuleLogger('[LeadDetails]');

export const useLeadDetails = (lead: Lead | null, _onSave: (lead: Lead) => void) => {
    const { funnelStages, saveLead } = useLeads();
    const [localLead, setLocalLead] = useState<Lead | null>(lead);
    const localLeadRef = useRef<Lead | null>(lead);
    const hasChanges = useRef(false);
    const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        setLocalLead(lead);
        hasChanges.current = false;
    }, [lead]);

    useEffect(() => {
        localLeadRef.current = localLead;
    }, [localLead]);

    const handleSaveAndClose = async (onCloseCallback: () => void) => {
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
            saveTimeoutRef.current = null;
        }

        // Ajuda em touch: força commit de foco antes de ler o snapshot (campos que dependem de blur).
        const active = document.activeElement as HTMLElement | null;
        active?.blur?.();
        await Promise.resolve();

        const snapshot = localLeadRef.current;
        if (snapshot && hasChanges.current) {
            const saved = await saveLead(snapshot);
            if (!saved) {
                return;
            }
            hasChanges.current = false;
        }
        onCloseCallback();
    };

    const handleObservationsDraft = useCallback((html: string) => {
        setLocalLead((prev) => (prev ? { ...prev, observations: html } : null));
        hasChanges.current = true;
    }, []);

    const handleInputChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
    ) => {
        if (!localLead) return;
        const { name, value } = e.target;

        let processedValue: any = value;

        if (name === 'value') {
            processedValue = parseFloat(value) || 0;
        } else if (name === 'tags') {
            try {
                processedValue = JSON.parse(value);
            } catch (error) {
                log.error('Error parsing tags:', error);
                processedValue = [];
            }
        }

        const updatedLead = {
            ...localLead,
            [name]: processedValue,
        };

        setLocalLead(updatedLead);

        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }

        if (name === 'name') {
            hasChanges.current = true;
            saveTimeoutRef.current = setTimeout(() => {
                void saveLead(updatedLead).then((saved) => {
                    if (saved) hasChanges.current = false;
                });
            }, 1000);
        } else if (name === 'stage' || name === 'agent' || name === 'value' || name === 'tags' || name === 'observations') {
            void saveLead(updatedLead).then((saved) => {
                if (saved) hasChanges.current = false;
            });
        } else {
            hasChanges.current = true;
        }
    };

    const handleMarkAsWon = async () => {
        if (!localLead) return;
        const wonStageId = funnelStages.find((s) => s.id === 'negocio-concluido')?.id;
        const target = wonStageId ? { ...localLead, stage: wonStageId } : localLead;
        if (wonStageId) setLocalLead(target);
        await saveLead(target);
    };

    const handleUpdateLeadValue = async (newValue: number) => {
        if (!localLead) return;
        const updatedLead = { ...localLead, value: newValue };
        setLocalLead(updatedLead);
        await saveLead(updatedLead);
    };

    return {
        localLead,
        funnelStages,
        handleInputChange,
        handleMarkAsWon,
        handleUpdateLeadValue,
        handleSaveAndClose,
        handleObservationsDraft,
        setLocalLead,
    };
};
